/**
 * /p/:chave — pagina publica da peca. Destino do QR colado na etiqueta.
 *
 * Metade de cima: a peca em 3D, girável pelo dedo, mostrando em quais topos
 * vai fita. Metade de baixo: tudo que foi preenchido na solicitacao, mais as
 * fotos anexadas.
 *
 * SEM LOGIN e SEM AuthContext — quem abre isto e um montador com o celular
 * na mao, muitas vezes de outra empresa. Fala com a API por `fetch` direto,
 * como as outras paginas /p/ do projeto.
 *
 * As fotos NAO vem na primeira resposta (seriam ate ~2 MB de base64 no 4G do
 * galpao): a lista traz so os ponteiros e cada foto e buscada quando o
 * usuario toca nela.
 *
 * Tema escuro fixo, laranja `sunset` — ver `styles.jsx` para o porque de as
 * cores serem cruas aqui.
 */
import React, {
  Suspense, lazy, useCallback, useEffect, useState,
} from "react";
import { useParams } from "react-router-dom";

import { ACCESS_API_URL } from "../../config/api";
import { MOTIVOS_PECA } from "../../services/solicitacoes";
import { LADOS_PECA } from "../Suprimentos/Solicitacoes/utils";
import {
  Bloco, ChipFita, Dados, Dica, Estado, Fechar, FotoAberta, FotoBotao,
  Fotos, Grade, ListaFita, Pagina, PalcoEsqueleto, Selo, TituloBloco, Topo,
} from "./styles";

// Carregamento tardio DE PROPÓSITO: o Peca3D arrasta o three.js (~150 KB
// gzip) e só esta página usa. Com o lazy, o webpack corta um chunk próprio
// e o resto do ERP não baixa um byte disso.
const Peca3D = lazy(() => import("./Peca3D"));

const MOTIVO_LABEL = Object.fromEntries(
  MOTIVOS_PECA.map((m) => [m.value, m.label]),
);

const NATUREZA_LABEL = {
  NOVA: "Peça nova",
  REPOSICAO: "Reposição",
  AJUSTE: "Ajuste",
};

/** "1000.0" -> "1000" ; "649.5" -> "649,5" ; vazio -> "—". */
function medida(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return Number.isInteger(n) ? String(n) : n.toLocaleString("pt-BR");
}

const ou = (v) => (v == null || v === "" ? "—" : v);

function Campo({ rotulo, valor }) {
  return (
    <div>
      <dt>{rotulo}</dt>
      <dd>{ou(valor)}</dd>
    </div>
  );
}

export default function PecaPublica() {
  const { chave } = useParams();
  const [peca, setPeca] = useState(null);
  const [erro, setErro] = useState(null);
  const [fotoAberta, setFotoAberta] = useState(null);
  // id da foto -> dataURL. Carregada so quando o usuario toca.
  const [imagens, setImagens] = useState({});
  const [carregandoFoto, setCarregandoFoto] = useState(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const resp = await fetch(`${ACCESS_API_URL}/publico/peca/${chave}`);
        if (!ativo) return;
        if (!resp.ok) {
          // A API devolve o MESMO erro para inexistente, revogado e chave de
          // outro cliente — de proposito. Aqui a mensagem tambem e uma so.
          setErro(resp.status === 429 ? "excesso" : "nao_encontrada");
          return;
        }
        setPeca(await resp.json());
      } catch {
        if (ativo) setErro("rede");
      }
    })();
    return () => { ativo = false; };
  }, [chave]);

  const abrirFoto = useCallback(async (foto) => {
    if (imagens[foto.id]) {
      setFotoAberta(foto.id);
      return;
    }
    setCarregandoFoto(foto.id);
    try {
      const resp = await fetch(
        `${ACCESS_API_URL}/publico/peca/${chave}/foto/${foto.id}`);
      if (!resp.ok) return;
      const dado = await resp.json();
      setImagens((atual) => ({ ...atual, [foto.id]: dado.imagem }));
      setFotoAberta(foto.id);
    } catch {
      /* falha de rede na foto nao derruba a pagina — o resto segue util */
    } finally {
      setCarregandoFoto(null);
    }
  }, [chave, imagens]);

  // Esc fecha a foto aberta.
  useEffect(() => {
    if (!fotoAberta) return undefined;
    const aoTeclar = (e) => { if (e.key === "Escape") setFotoAberta(null); };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [fotoAberta]);

  if (erro) {
    const textos = {
      nao_encontrada: [
        "Peça não encontrada",
        "Este código não corresponde a nenhuma peça ativa. Confira se o QR "
        + "foi lido por inteiro.",
      ],
      excesso: [
        "Muitas consultas",
        "Aguarde um minuto e escaneie de novo.",
      ],
      rede: [
        "Sem conexão",
        "Não deu para falar com o servidor. Verifique a internet e tente "
        + "de novo.",
      ],
    }[erro];
    return (
      <Estado>
        <strong>{textos[0]}</strong>
        <span>{textos[1]}</span>
      </Estado>
    );
  }

  if (!peca) {
    return <Estado><span>Carregando a peça…</span></Estado>;
  }

  return (
    <Pagina>
      <Topo>
        <span className="num">{peca.solicitacao_numero}</span>
        <span className="desc">
          {[peca.descricao, peca.ambiente].filter(Boolean).join(" · ")}
        </span>
        {peca.lote_numero && <Selo>LOTE {peca.lote_numero}</Selo>}
      </Topo>

      <Suspense fallback={<PalcoEsqueleto>Preparando o 3D…</PalcoEsqueleto>}>
        <Peca3D peca={peca} />
      </Suspense>

      <Dica>Gira sozinho até você tocar — arraste para girar · pince para aproximar</Dica>

      {/* TODOS os dados num bloco só, sem seções — pedido do dono
          (2026-08-13): "os dados precisam estar juntos". Os chips de fita
          abrem o bloco porque são a resposta rápida; o resto é uma grade
          única e contínua. */}
      <Dados>
        <Bloco>
          <ListaFita>
            {LADOS_PECA.map(({ campo, rotulo }) => (
              <ChipFita key={campo} $tem={Boolean(peca[campo])}>
                {rotulo}{peca[campo] ? "" : " — sem fita"}
              </ChipFita>
            ))}
          </ListaFita>

          <Grade style={{ marginTop: 12 }}>
            <Campo rotulo="Largura" valor={`${medida(peca.largura_mm)} mm`} />
            <Campo
              rotulo="Comprimento (veio)"
              valor={`${medida(peca.comprimento_mm)} mm`}
            />
            <Campo rotulo="Espessura" valor={`${medida(peca.espessura_mm)} mm`} />
            <Campo rotulo="Quantidade" valor={peca.quantidade} />
            {peca.fita_nome && <Campo rotulo="Fita" valor={peca.fita_nome} />}
            <Campo rotulo="Cor / material" valor={peca.material_nome} />
            <Campo rotulo="Código" valor={peca.material_codigo} />
            <Campo rotulo="Acabamento" valor={peca.acabamento} />
            <Campo rotulo="Descrição" valor={peca.descricao} />
            <Campo rotulo="Ambiente" valor={peca.ambiente} />
            <Campo rotulo="Módulo" valor={peca.modulo} />
            <Campo rotulo="Referência" valor={peca.ref_modulo} />
            <Campo rotulo="ID da peça" valor={peca.codigo_peca} />
            <Campo
              rotulo="Natureza"
              valor={NATUREZA_LABEL[peca.natureza] || peca.natureza}
            />
            <Campo
              rotulo="Motivo"
              valor={MOTIVO_LABEL[peca.motivo_categoria] || peca.motivo_categoria}
            />
            <Campo rotulo="Detalhe do motivo" valor={peca.motivo_detalhe} />
            <Campo rotulo="Observação" valor={peca.observacao} />
            {peca.usinagem && (
              <Campo rotulo="Usinagem" valor={ou(peca.usinagem_descricao)} />
            )}
            {peca.pintura && (
              <Campo rotulo="Pintura" valor={ou(peca.pintura_descricao)} />
            )}
            <Campo rotulo="Número" valor={peca.solicitacao_numero} />
            <Campo rotulo="Tipo" valor={peca.solicitacao_tipo} />
            <Campo rotulo="Cliente" valor={peca.cliente_nome} />
            <Campo rotulo="Pedido" valor={peca.num_pedido} />
            <Campo rotulo="Solicitante" valor={peca.solicitante_nome} />
            <Campo rotulo="Supervisor" valor={peca.supervisor_nome} />
            <Campo rotulo="Líder de montagem" valor={peca.lider_montagem_nome} />
            <Campo rotulo="Aberta em" valor={peca.solicitada_em} />
            <Campo rotulo="Lote" valor={peca.lote_numero} />
          </Grade>

          {(peca.fotos || []).length > 0 && (
            <>
              <TituloBloco style={{ margin: "16px 0 10px" }}>
                Fotos ({peca.fotos.length})
              </TituloBloco>
              <Fotos>
                {peca.fotos.map((foto, i) => (
                  <FotoBotao
                    key={foto.id}
                    type="button"
                    onClick={() => abrirFoto(foto)}
                    title={foto.legenda || `Foto ${i + 1}`}
                  >
                    {imagens[foto.id] ? (
                      <img src={imagens[foto.id]} alt={foto.legenda || `Foto ${i + 1}`} />
                    ) : (
                      <span className="ph">
                        {carregandoFoto === foto.id ? "Abrindo…" : `Foto ${i + 1}`}
                      </span>
                    )}
                  </FotoBotao>
                ))}
              </Fotos>
            </>
          )}
        </Bloco>
      </Dados>

      {fotoAberta && imagens[fotoAberta] && (
        <FotoAberta onClick={() => setFotoAberta(null)}>
          <img src={imagens[fotoAberta]} alt="Foto da peça" />
          <Fechar type="button" aria-label="Fechar foto"
            onClick={() => setFotoAberta(null)}>×</Fechar>
        </FotoAberta>
      )}
    </Pagina>
  );
}
